import React, { useEffect, useMemo, useRef, useState } from "react";
import * as echarts from "echarts";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";

const formatUtcToBrowserDate = (time) => {
  if (!time) return null;

  const normalizedTime =
    typeof time === "string" && !time.endsWith("Z") ? `${time}Z` : time;

  return dayjs(normalizedTime);
};

const WiredCongestionLevelSliderChart = ({
  data = [],
  defaultThreshold,
  externalThreshold,
  onThresholdChange,
  wiredCongestionApiChartData,
}) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const Y_AXIS_MIN = 0;

  const portCount = Number(wiredCongestionApiChartData?.portCount) || 0;

  const chartThreshold = Number(externalThreshold ?? defaultThreshold) || 0;

  const [inputThreshold, setInputThreshold] = useState(chartThreshold);

  const chartData = useMemo(() => {
    const series = Array.isArray(data) ? data : (data?.series ?? []);
    const dataMap = new Map();

    series.forEach((item) => {
      if (!Array.isArray(item)) return;

      const [time, value] = item;
      const browserTime = formatUtcToBrowserDate(time);

      if (!browserTime?.isValid()) return;

      const key = browserTime.format("YYYY-MM-DD HH");
      dataMap.set(key, Number(value) || 0);
    });

    const lastTime =
      series.length > 0
        ? formatUtcToBrowserDate(series[series.length - 1]?.[0])
        : dayjs().startOf("hour");

    const endTime =
      lastTime?.isValid() === true
        ? lastTime.startOf("hour")
        : dayjs().startOf("hour");

    const startTime = endTime.subtract(23, "hour");

    return Array.from({ length: 24 }, (_, index) => {
      const currentTime = startTime.add(index, "hour");
      const key = currentTime.format("YYYY-MM-DD HH");

      return {
        label: currentTime.format("HH:mm"),
        fullTime: currentTime.format("YYYY/MM/DD HH:mm"),
        value: dataMap.get(key) ?? 0,
      };
    });
  }, [data]);

  const dynamicMax = useMemo(() => {
    const series = Array.isArray(data) ? data : (data?.series ?? []);
    const maxValue = Math.max(
      ...series.map((item) => Number(item?.[1]) || 0),
      0,
    );

    return Math.max(maxValue, chartThreshold) || 10;
  }, [data, chartThreshold]);

  const option = useMemo(() => {
    const xAxisData = chartData.map((item) => item.label);
    const yAxisData = chartData.map((item) => item.value);

    return {
      animation: false,
      backgroundColor: theme.palette.primary.white,
      grid: {
        top: 20,
        right: 24,
        bottom: 36,
        left: 42,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: theme.palette.primary.blackTransparency,
        borderWidth: 0,
        textStyle: {
          color: theme.palette.primary.white,
          fontSize: theme.typography.customFontSize[0],
        },
        formatter: (params) => {
          const point = params?.[0];
          if (!point) return "";
          return `${point.axisValue}<br/>Congested Ports: ${point.data}`;
        },
        axisPointer: {
          type: "line",
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
            type: "dashed",
          },
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: xAxisData,
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          margin: 12,
          interval: 0,
          formatter: (value, index) => {
            const isLast = index === xAxisData.length - 1;
            return index % 4 === 0 || isLast ? value : "";
          },
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: true,
          interval: 0,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
            type: "dashed",
          },
        },
      },
      yAxis: {
        type: "value",
        min: Y_AXIS_MIN,
        max: dynamicMax,
        minInterval: 1,
        splitNumber: 5,
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          margin: 12,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
            type: "dashed",
          },
        },
      },
      series: [
        {
          name: "Congested Ports",
          type: "line",
          data: yAxisData,
          smooth: false,
          showSymbol: false,
          symbol: "none",
          connectNulls: true,
          lineStyle: {
            color: theme.palette.primary.main,
            width: 2.2,
          },
        },
      ],
    };
  }, [chartData, theme, dynamicMax]);

  useEffect(() => {
    let nextValue = chartThreshold;

    if (nextValue < Y_AXIS_MIN) nextValue = Y_AXIS_MIN;
    if (nextValue > portCount) nextValue = portCount;

    setInputThreshold(nextValue);
  }, [chartThreshold, portCount]);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart =
      echarts.getInstanceByDom(chartRef.current) ||
      echarts.init(chartRef.current);

    chartInstanceRef.current = chart;
    chart.setOption(option, true);
    chart.resize();

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [option]);

  return (
    <div style={{ width: "100%" }}>
      <div
        ref={chartRef}
        style={{
          width: "100%",
          minWidth: 0,
          height: "320px",
        }}
      />

      <div
        style={{
          marginLeft: 42,
          marginRight: 24,
          marginTop: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 8,
            fontSize: theme.typography.customFontSize[2],
            color: theme.palette.primary.greyPrimary,
          }}
        >
          <div>
            Config: <strong>Congested Ports Threshold</strong>
          </div>

          <strong>{portCount}</strong>
        </div>

        <input
          type="number"
          min={Y_AXIS_MIN}
          max={portCount}
          value={inputThreshold}
          onChange={(e) => {
            let nextValue = Number(e.target.value);

            if (Number.isNaN(nextValue)) nextValue = Y_AXIS_MIN;
            if (nextValue < Y_AXIS_MIN) nextValue = Y_AXIS_MIN;
            if (nextValue > portCount) nextValue = portCount;

            setInputThreshold(nextValue);
            onThresholdChange?.(nextValue);
          }}
          style={{
            width: 80,
            height: 36,
            padding: "0 12px",
            border: "1px solid #D9D9D9",
            borderRadius: 4,
            fontSize: theme.typography.customFontSize[2],
            color: theme.palette.primary.greyPrimary,
          }}
        />
      </div>
    </div>
  );
};

export default WiredCongestionLevelSliderChart;
